import type { InsuranceChecklist, Post } from '../types';
import { topicForItem } from '../constants/checklistTopics';

// Group a structure's posts by the checklist item they document. Keyed by the
// raw item id the post was filed under, which may belong to a model the
// structure has since switched away from — see groupEvidenceForChecklist.
export function groupEvidenceByItem(posts: Post[]): Map<string, Post[]> {
  const byItem = new Map<string, Post[]>();
  for (const post of posts) {
    if (!post.checklistItemId) continue;
    const list = byItem.get(post.checklistItemId);
    if (list) {
      list.push(post);
    } else {
      byItem.set(post.checklistItemId, [post]);
    }
  }
  return byItem;
}

export interface ItemEvidence {
  /** Posts filed against this exact item id. */
  direct: Post[];
  /** Posts filed under another model's item that documents the same work. */
  related: Post[];
}

function createdAtMs(post: Post): number {
  const value = post.createdAt as { toMillis?: () => number } | undefined;
  return typeof value?.toMillis === 'function' ? value.toMillis() : 0;
}

/**
 * Evidence for every item in the checklist currently on screen, unified by
 * topic.
 *
 * A post keeps its original `checklistItemId` forever. When that item belongs
 * to a model the structure has switched away from, the post still surfaces
 * under whichever item in the CURRENT model documents the same work. Items
 * with no topic fall back to exact item-id match, which is how agent-authored
 * models keep behaving exactly as they do today.
 */
export function groupEvidenceForChecklist(
  checklist: Pick<InsuranceChecklist, 'items'> | null | undefined,
  posts: Post[]
): Map<string, ItemEvidence> {
  const result = new Map<string, ItemEvidence>();
  if (!checklist) return result;

  const byItem = groupEvidenceByItem(posts);
  const renderedIds = new Set(checklist.items.map((i) => i.id));

  // Only posts whose own item is absent from this checklist can be borrowed.
  // Excluding rendered ids is what stops a post being `direct` under one row
  // and `related` under a sibling row sharing its topic.
  const byTopic = new Map<string, Post[]>();
  for (const [itemId, itemPosts] of byItem) {
    if (renderedIds.has(itemId)) continue;
    const topic = topicForItem(itemId);
    if (!topic) continue;
    const bucket = byTopic.get(topic);
    if (bucket) {
      bucket.push(...itemPosts);
    } else {
      byTopic.set(topic, [...itemPosts]);
    }
  }

  for (const item of checklist.items) {
    // The stored value wins over the static table so a taxonomy correction
    // takes effect without shipping a new bundle.
    const topic = item.topic ?? topicForItem(item.id);
    const direct = byItem.get(item.id) ?? [];
    // Posts arrive newest-first per item, but merging buckets from several
    // source ids loses that order.
    const related = topic
      ? [...(byTopic.get(topic) ?? [])].sort((a, b) => createdAtMs(b) - createdAtMs(a))
      : [];
    if (direct.length === 0 && related.length === 0) continue;
    result.set(item.id, { direct, related });
  }

  return result;
}

export function computeProgressPercent(completedCount: number, totalCount: number): number {
  return totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
}
